/**
 * Domain-event WebSocket client.
 *
 * This channel carries domain events only. Terminal bytes and host telemetry
 * have their own connections (ADR-0004) because a browser watching a fast build
 * scroll past must never apply backpressure to an approval notification.
 *
 * The protocol is snapshot-then-delta with a cursor, and this file is written
 * against the server's definition in `internal/realtime/protocol.go` — the
 * frame kinds, field names and connect parameters here must match that file
 * exactly. They previously did not, and the result was a console that connected
 * successfully and then silently received nothing.
 *
 * Two frames are easy to overlook and both matter:
 *
 *   `checkpoint` advances the cursor past events the server filtered out. A
 *   narrowly-subscribed client would otherwise never move its cursor, and every
 *   reconnect would replay its whole subscription from the beginning.
 *
 *   `resnapshot` says the cursor can no longer be served. The client must take
 *   a fresh snapshot rather than carry a gap it has no way to detect.
 */

import type { DomainEvent } from '@/types/domain'
import { createWatchdog, type Watchdog } from '@/lib/liveness'

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed'

/** Frame kinds the server sends. Mirrors internal/realtime/protocol.go. */
const KIND_SNAPSHOT = 'snapshot'
const KIND_DELTA = 'delta'
const KIND_STATE = 'state'
const KIND_RESNAPSHOT = 'resnapshot'
const KIND_CHECKPOINT = 'checkpoint'
const KIND_ERROR = 'error'
const KIND_PONG = 'pong'

/**
 * The hub's application close codes, from internal/realtime/realtime.go, where
 * they are declared to be a wire contract on the grounds that "a browser reads
 * these to decide whether to reconnect". No browser did. Every close was treated
 * as a transient one, so a session that ended mid-stream — the hub re-checks the
 * credential on a timer and says so with a code that means "reconnecting without
 * re-authenticating will fail again" — produced an infinite, silent reconnect
 * loop against a server that would refuse every attempt, with the console
 * showing "reconnecting" and no reason, forever.
 */
const CLOSE_SLOW_CONSUMER = 4001
const CLOSE_UNAUTHORISED = 4002

/** The wire version the server stamps on its snapshot. */
export const PROTOCOL_VERSION = 1

/**
 * One frame shape rather than a union, matching the server's ServerFrame.
 * A single discriminated shape is cheaper to reduce over in a browser than a
 * per-kind decoder, which is why the server sends it this way.
 */
interface ServerFrame {
  kind: string
  version?: number
  cursor?: number
  latest?: number
  event?: DomainEvent
  snapshot?: unknown
  reason?: string
  serverTime?: string
}

interface ClientFrame {
  kind: 'subscribe' | 'ping'
  filter?: FilterSpec
}

/** Mirrors realtime.FilterSpec. Field names are the server's, not the UI's. */
export interface FilterSpec {
  projects?: string[]
  sessions?: string[]
  types?: string[]
  minSeverity?: string
}

export interface EventStreamHandlers {
  onSnapshot?: (data: unknown, cursor: number, latest: number) => void
  /** A refreshed pool document that is not an event: no cursor, not resumable,
   *  safe to miss. It carries what a domain event deliberately never will —
   *  host telemetry — because archiving four CPU readings per host per minute
   *  would bury the incidents the event log exists to keep (ADR-0004). */
  onState?: (data: unknown) => void
  onEvent?: (event: DomainEvent) => void
  onStateChange?: (state: ConnectionState) => void
  onError?: (message: string) => void
  /** Reports how far behind the archive this client is, for a lag indicator. */
  onLag?: (behind: number) => void
  /** Fires when the server and client disagree about the protocol version,
   *  which means a stale cached bundle is talking to a newer server. */
  onVersionMismatch?: (serverVersion: number) => void
  /** Fires when the hub says the credential behind this connection has ended.
   *  Reconnecting cannot fix it, so the stream stops and the app decides. */
  onUnauthorised?: () => void
}

const MAX_BACKOFF_MS = 30_000
const BASE_BACKOFF_MS = 500
const HEARTBEAT_MS = 25_000

/* Liveness. An idle hub sends nothing unsolicited — a checkpoint is only
 * offered when the cursor has actually moved — so the pong answering the
 * heartbeat above is the one frame this channel can count on. The silence
 * budget therefore has to clear a whole heartbeat period with room for one
 * lost round trip. */
const SILENCE_MS = 40_000
const PROBE_MS = 10_000
const LIVENESS_TICK_MS = 5_000

export class EventStream {
  private ws: WebSocket | null = null
  private state: ConnectionState = 'closed'
  /** Undefined means "no resumable position": the next connect asks for a
   *  full snapshot rather than a resume. */
  private cursor: number | undefined
  private latest = 0
  private filter: FilterSpec = {}
  private attempt = 0
  private heartbeat: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private watchdog: Watchdog | null = null
  private stopped = false

  /**
   * Bounded window of recently applied event ids.
   *
   * The server guarantees at-least-once, so a duplicate is normal traffic
   * rather than an anomaly. The window is bounded because an unbounded set in a
   * console tab left open for a week is a slow memory leak.
   */
  private readonly seen = new Set<string>()
  private readonly seenOrder: string[] = []
  private static readonly SEEN_LIMIT = 2000

  constructor(private readonly handlers: EventStreamHandlers) {}

  connect(filter: FilterSpec = {}): void {
    this.filter = filter
    this.stopped = false
    this.open()
  }

  /** Replaces the subscription without dropping the connection. */
  updateFilters(filter: FilterSpec): void {
    this.filter = filter
    this.send({ kind: 'subscribe', filter })
  }

  close(): void {
    this.stopped = true
    this.clearTimers()
    this.ws?.close(1000, 'client closed')
    this.ws = null
    this.setState('closed')
  }

  getState(): ConnectionState {
    return this.state
  }

  /** The cursor this client would resume from, for diagnostics. */
  getCursor(): number | undefined {
    return this.cursor
  }

  /**
   * Builds the handshake URL.
   *
   * The server reads the resume cursor and the initial filter from query
   * parameters, so a reconnect resumes on the very first frame rather than
   * after a round trip. Note the parameter names are singular and repeated
   * (`project=a&project=b`), which is what `queryList` on the server expects.
   */
  private url(): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const params = new URLSearchParams()
    if (this.cursor !== undefined) params.set('cursor', String(this.cursor))
    for (const p of this.filter.projects ?? []) params.append('project', p)
    for (const s of this.filter.sessions ?? []) params.append('session', s)
    for (const t of this.filter.types ?? []) params.append('type', t)
    if (this.filter.minSeverity) params.set('minSeverity', this.filter.minSeverity)

    const qs = params.toString()
    return `${proto}//${window.location.host}/ws/events${qs ? `?${qs}` : ''}`
  }

  private open(): void {
    if (this.stopped) return
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting')

    const ws = new WebSocket(this.url())
    this.ws = ws

    // Both the peer's close frame and the watchdog's verdict end the connection
    // through here, and the latch makes it happen once — a condemned socket
    // usually fires onclose shortly afterwards, and two reconnects would leave
    // one stream unreferenced and still delivering.
    let ended = false
    const end = (code?: number) => {
      if (ended) return
      ended = true
      this.clearTimers()
      if (this.stopped) {
        this.setState('closed')
        return
      }
      if (code === CLOSE_UNAUTHORISED) {
        // Retrying is guaranteed to fail and guarantees the operator is never
        // told why. Stop, and hand the decision to whoever can act on it.
        this.stopped = true
        this.setState('closed')
        this.handlers.onUnauthorised?.()
        return
      }
      if (code === CLOSE_SLOW_CONSUMER) {
        // The hub dropped frames this client never saw. Resuming from a cursor
        // would silently skip them; the contract asks for a fresh snapshot, and
        // an undefined cursor is how this asks for one.
        this.cursor = undefined
        this.seen.clear()
        this.seenOrder.length = 0
      }
      this.scheduleReconnect()
    }

    const watchdog = createWatchdog({
      silenceMs: SILENCE_MS,
      probeMs: PROBE_MS,
      tickMs: LIVENESS_TICK_MS,
      probe: () => this.send({ kind: 'ping' }),
      onDead: () => {
        // Reconnect first, close second: close() negotiates with a peer that is
        // not answering, and the browser will hold CLOSING until its own
        // timeout — the very wait this exists to avoid.
        end(undefined)
        try {
          ws.close(4000, 'no answer')
        } catch {
          // Already gone; the reconnect above is what mattered.
        }
      },
    })
    this.watchdog = watchdog

    ws.onopen = () => {
      this.attempt = 0
      this.setState('open')
      // The filter also travels in the URL, but re-sending it makes the
      // subscription explicit and is what updateFilters uses thereafter.
      this.send({ kind: 'subscribe', filter: this.filter })
      this.startHeartbeat()
      // Only now: a socket still in CONNECTING has nothing to probe with, and
      // its handshake is not silence.
      watchdog.start()
    }

    ws.onmessage = (ev) => {
      // Counted before parsing, and for a frame this build cannot read: what
      // proves the path is alive is that bytes arrived, not what they meant.
      watchdog.alive()
      let msg: ServerFrame
      try {
        msg = JSON.parse(ev.data as string) as ServerFrame
      } catch {
        // A malformed frame is the server's problem, not a reason to tear down
        // an otherwise working connection.
        this.handlers.onError?.('Received a malformed message.')
        return
      }
      this.handle(msg)
    }

    ws.onerror = () => {
      // onclose always follows, and that is where reconnection is handled.
    }

    // The code is the point: see CLOSE_UNAUTHORISED above.
    ws.onclose = (ev?: { code?: number }) => end(ev?.code)
  }

  private handle(msg: ServerFrame): void {
    switch (msg.kind) {
      case KIND_SNAPSHOT: {
        if (msg.version !== undefined && msg.version !== PROTOCOL_VERSION) {
          // A stale cached bundle against a newer server. Reporting it beats
          // misinterpreting frames, which is what the version field is for.
          this.handlers.onVersionMismatch?.(msg.version)
        }
        // A fresh snapshot supersedes everything before it, so the duplicate
        // window is cleared: an id from before the snapshot cannot arrive again
        // as part of the new stream, and keeping it would suppress a real event.
        this.seen.clear()
        this.seenOrder.length = 0
        this.cursor = msg.cursor ?? 0
        this.latest = msg.latest ?? this.cursor
        this.handlers.onSnapshot?.(msg.snapshot, this.cursor, this.latest)
        this.reportLag()
        break
      }

      case KIND_STATE:
        // Deliberately touches neither the cursor nor the duplicate window: a
        // state frame is a photograph, not a step in a sequence. Advancing the
        // cursor here would tell the server this client had consumed events it
        // has never seen.
        this.handlers.onState?.(msg.snapshot)
        break

      case KIND_DELTA: {
        const e = msg.event
        if (!e) return
        if (this.seen.has(e.id)) {
          // A duplicate still advances the cursor: the server has moved on, and
          // refusing to follow would replay from here on the next reconnect.
          this.advance(msg.cursor)
          return
        }
        this.remember(e.id)
        this.advance(msg.cursor ?? e.sequence)
        this.handlers.onEvent?.(e)
        break
      }

      case KIND_CHECKPOINT:
        // Events were filtered out server-side. Following the cursor is the
        // whole point — without it a narrow subscription never advances.
        this.advance(msg.cursor)
        if (msg.latest !== undefined) {
          this.latest = msg.latest
          this.reportLag()
        }
        break

      case KIND_RESNAPSHOT:
        // The server cannot serve from our cursor. Dropping it forces a full
        // snapshot on the next connect, which is correct: continuing would
        // leave a gap the client has no way to detect.
        this.cursor = undefined
        this.handlers.onError?.(msg.reason ?? 'Resynchronising.')
        this.send({ kind: 'subscribe', filter: this.filter })
        break

      case KIND_ERROR:
        this.handlers.onError?.(msg.reason ?? 'The server reported a problem.')
        break

      case KIND_PONG:
        // Nothing to apply — but its *arrival* was counted as liveness above,
        // which is the entire reason the heartbeat asks for it. It used to be
        // discarded here, so the ping measured nothing and a silently dropped
        // connection stayed 'open' until the page was reloaded.
        break

      default:
        // An unrecognised kind from a newer server is version skew to survive,
        // not a reason to drop a working connection.
        break
    }
  }

  /** Moves the cursor forward only. An out-of-order redelivery must never
   *  rewind it, or the next reconnect replays everything after it again. */
  private advance(cursor: number | undefined): void {
    if (cursor === undefined) return
    if (this.cursor === undefined || cursor > this.cursor) {
      this.cursor = cursor
      this.reportLag()
    }
  }

  private reportLag(): void {
    if (this.cursor === undefined) return
    this.handlers.onLag?.(Math.max(0, this.latest - this.cursor))
  }

  private remember(id: string): void {
    this.seen.add(id)
    this.seenOrder.push(id)
    if (this.seenOrder.length > EventStream.SEEN_LIMIT) {
      const evicted = this.seenOrder.shift()
      if (evicted) this.seen.delete(evicted)
    }
  }

  private send(msg: ClientFrame): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat()
    // An application-level ping detects a connection TCP still believes is
    // alive — a proxy that dropped it silently sends no close frame.
    this.heartbeat = setInterval(() => this.send({ kind: 'ping' }), HEARTBEAT_MS)
  }

  private scheduleReconnect(): void {
    this.setState('reconnecting')
    // Exponential backoff with jitter, so a server restart does not produce a
    // synchronised reconnect storm from every open console tab.
    const base = Math.min(BASE_BACKOFF_MS * 2 ** this.attempt, MAX_BACKOFF_MS)
    const delay = base * (0.5 + Math.random() * 0.5)
    this.attempt += 1
    this.reconnectTimer = setTimeout(() => this.open(), delay)
  }

  private clearHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat)
      this.heartbeat = null
    }
  }

  private clearTimers(): void {
    this.clearHeartbeat()
    this.watchdog?.stop()
    this.watchdog = null
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * Re-examines the connection immediately, for a caller that has just learned
   * something the socket has not: the tab became visible again, or the network
   * came back. Those are the two moments a connection is most likely to have
   * died unobserved, and waiting out the ordinary silence budget at either one
   * shows the operator a console that has quietly stopped updating.
   */
  probe(): void {
    this.watchdog?.poke()
  }

  private setState(s: ConnectionState): void {
    if (this.state === s) return
    this.state = s
    this.handlers.onStateChange?.(s)
  }
}
